import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'

interface ProposeMeetingProps {
  bookPickId: number
  onClose: () => void
  onSuccess: () => void
}

interface ProposedDate {
  datetime: string
  time: string
  location: string
}

export default function ProposeMeeting({ bookPickId, onClose, onSuccess }: ProposeMeetingProps) {
  const [proposedDates, setProposedDates] = useState<ProposedDate[]>([
    { datetime: '', time: '', location: '' },
    { datetime: '', time: '', location: '' },
  ])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const addDateSlot = () => {
    setProposedDates([...proposedDates, { datetime: '', time: '', location: '' }])
  }

  const removeDateSlot = (index: number) => {
    if (proposedDates.length > 1) {
      setProposedDates(proposedDates.filter((_, i) => i !== index))
    }
  }

  const updateDate = (index: number, field: keyof ProposedDate, value: string) => {
    const updated = [...proposedDates]
    updated[index] = { ...updated[index], [field]: value }
    setProposedDates(updated)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate at least one complete date
    const validDates = proposedDates.filter(d => d.datetime && d.time)
    if (validDates.length === 0) {
      setError('Please add at least one date and time')
      return
    }

    setSubmitting(true)

    try {
      // Combine date and time into datetime string
      const dates = validDates.map(d => ({
        proposed_datetime: `${d.datetime}T${d.time}:00`,
        location: d.location || null
      }))

      const response = await fetchWithCsrf(`/api/v1/book_picks/${bookPickId}/meeting_proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meeting_proposal: {
            proposed_dates: dates
          }
        })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.error || data.errors?.join(', ') || 'Failed to create proposal')
      }
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true} className="overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Propose Meeting Dates</h2>
            <p className="text-gray-600">Add multiple dates for the group to vote on</p>
          </div>
        </div>
      </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {proposedDates.map((date, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-gray-900">Option {index + 1}</h3>
                  {proposedDates.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDateSlot(index)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={date.datetime}
                      onChange={(e) => updateDate(index, 'datetime', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time *
                    </label>
                    <input
                      type="time"
                      value={date.time}
                      onChange={(e) => updateDate(index, 'time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location (optional)
                  </label>
                  <input
                    type="text"
                    value={date.location}
                    onChange={(e) => updateDate(index, 'location', e.target.value)}
                    placeholder="e.g., Coffee Shop, Library, Online"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  />
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addDateSlot}
            className="mt-4 w-full py-2 border-2 border-dashed border-gray-300 hover:border-indigo-400 text-gray-600 hover:text-indigo-600 font-medium rounded-lg transition-colors"
          >
            + Add Another Date Option
          </button>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
            >
              {submitting ? 'Creating Proposal...' : 'Send to Group for Voting'}
            </button>
          </div>
        </form>
      </Modal>
  )
}
